import { memo, useEffect, useState } from "react";
import FindInVizDropdown from "./FindInVizDropdown";
import ClickAwayListener from "react-click-away-listener";
import { spacing } from "@atlas-int/tokens";
import styled from "@emotion/styled";

const SegmentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  position: relative;
  gap: ${spacing[1]};
  margin: 0 ${spacing[3]};
`;

const IconContainer = styled.div`
  height: auto;
`;

const SettingsButtonContainer = styled.button`
  text-transform: uppercase;
  display: flex;
  align-items: center;
  height: 100%;
  cursor: pointer;

  & img {
    width: ${spacing[10]};
    margin-right: ${spacing[2]};
  }
  border: none;
  background-color: transparent;
`;

export interface FindInVizOption {
  id: string;
  label: string;
}

interface FindInVizProps {
  /** Available search options */
  options: FindInVizOption[];
  /** Currently highlighted item ID */
  highlightedItem: string | undefined;
  /** Called when highlighting changes */
  onHighlight: (id: string | undefined) => void;
  /** Whether the FindInViz control is visible */
  visible?: boolean;
  /** Placeholder text for the search input */
  placeholderText?: string;
  /** Icon source for the magnifying glass */
  iconSrc?: string;
  /** Callback when the search menu opens */
  onOpen?: () => void;
}

const FindInViz = ({
  options,
  highlightedItem,
  onHighlight,
  visible = true,
  placeholderText = "Find in graph",
  iconSrc,
  onOpen,
}: FindInVizProps) => {
  const [hovered, setHovered] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) {
      onOpen?.();
    }
  }, [open, onOpen]);

  if (!visible) {
    return <div style={{ width: spacing[20] }} />;
  }

  return (
    <SegmentContainer style={{ width: "220px" }}>
      <ClickAwayListener
        onClickAway={() => {
          setHovered(false);
          setOpen(false);
        }}
      >
        <div onMouseOut={() => !open && setHovered(false)}>
          {!hovered && !open && (
            <IconContainer
              onClick={() => !open && setOpen(true)}
              onMouseEnter={() => {
                if (!hovered) {
                  setHovered(true);
                }
              }}
            >
              <SettingsButtonContainer>
                {iconSrc && <img src={iconSrc} alt="Find in Visualization" />}{" "}
                Find in Viz
              </SettingsButtonContainer>
            </IconContainer>
          )}
          {(hovered || open) && (
            <div onClick={() => !open && setOpen(true)}>
              <FindInVizDropdown
                findInVizOptions={options}
                highlightedItem={highlightedItem}
                setHighlightedItem={onHighlight}
                setHovered={setHovered}
                setOpen={setOpen}
                placeholderText={placeholderText}
              />
            </div>
          )}
        </div>
      </ClickAwayListener>
    </SegmentContainer>
  );
};

export default memo(FindInViz);
